<template>
  <div class="container serp">
    <div class="serp__title-container">
      <div class="serp__title-left">
        <div class="serp__header-button-wrapper"
             v-if="isDataFetched">
          <button
            @click.stop="cancelSortingAndFilters"
            class="serp__clear-filters">
            <span>clear all filters and sorting</span>
            <span class="serp__cross">&times;</span>
          </button>
        </div>

      </div>
      <div class="serp__title-right"
           v-if="isDataFetched">
        <div class="serp__serps">
          <span class="serp__count">Indexations: {{totalItems}}</span>
          <base-select
            class="serp__grid-select"
            :options="paginationOptions"
            alignLeft
            :titleStyle="{'padding-left': '5px'}"
            :headerStyle="{}"
            showTopBorder
            shiftLabelRight
            v-model="pageItemsLimit"
            :selectStyle="{'max-height': '35px !important', width: '100px !important'}"
            @change="handleLimitChange"
          />
        </div>
        <button class="serp__button" @click="openCreateModal()">
          Create Indexation
        </button>
      </div>
    </div>

    <div class="common-table serp__table">
      <div class="common-table__row common-table__header"
           v-if="isDataFetched">
        <div class="common-table__header-cell"
             v-for="column in serpTableColumnsConfig"
             :key="column.property"
             :class="[`serp__${column.property}`]">
          <base-multi-select-sortable-header
            v-if="column.property !== 'serp-buttons'"
            class="serp__select-header"
            :column="column"
            :sort-config="sortConfig"
            :predefinedOptions="filterParams[column.property]"
            :predefinedDateRangeFilter="['last_ran' ].includes(column.property) ? {start: filterParams[column.property]['date_from'], end: filterParams[column.property]['date_to']}: null"
            :predefinedNumberFilter="['last_indexed_page'].includes(column.property) ? filterParams[column.property] : null"
            @sort="onHeaderCellClicked(column, $event)"
            @apply="filterColumn($event, column)"
            @cancel="cancelFilter($event, column)"
            :ref="sortableHeaderRefs[column.property]"
          />
        </div>
      </div>
      <template v-if="dataMissing">
        <div class="common-table__row no-data-block">
          No records found
        </div>
      </template>
      <div class="" v-else-if="!isDataFetched">
        <div class="serp__loader">
          <base-loader/>
        </div>
      </div>
      <template v-else>
        <template v-for="serp in paginatedSerps">
          <div :key="serp.id" class="common-table__row">
            <div class="common-table__cell"
                 v-for="column in serpTableColumnsConfig"
                 :key="column.property"
                 :class="[`serp__${column.property}`, `serp__row_${column.property}`]">
              <template v-if="column.property === 'serp-buttons'">
                <div>
                  <a @click="openCreateModal(serp.id)" class="edit-icon">
                    <base-material-design-icon icon="edit"/>
                  </a>
                </div>
                <div>
                  <a class="edit-icon" :href="`/serp_tracker/download_report?id=${serp.id}`">
                    <base-material-design-icon icon="fileDownload"/>
                  </a>
                </div>
                <div>
                  <router-link :to="`/system/indexation-tracking/${serp.id}`"
                               class="edit-icon">
                    <base-material-design-icon icon="chart"/>
                  </router-link>
                </div>
              </template>
              <template v-else-if="column.property === 'is_active'">
                  <span>
                    {{ serp[column.property] ? 'Yes' : 'No' }}
                  </span>
              </template>
              <template v-else>
                  <span>
                    {{ serp[column.property] }}
                  </span>
              </template>
            </div>
          </div>
        </template>
      </template>
    </div>
    <div class="pagination-wrapper" v-show="hasPagination">
      <base-pagination :limit="pageItemsLimit"
                       :total-items="totalItems"
                       @pageChanged="pageChanged"/>
    </div>
    <div class="serp__update-loader" v-if="isDataUpdating">
      <base-loader class="serp__update-loader-ring"/>
    </div>

    <modal name="create"
           v-bind="changeSerpModalProps">
      <CreateUpdateSerpPopup
        ref="create-serp"
        @apply="createOrUpdateSerp"
        @delete="deleteSerp"
        :selected-serp="selectedSerp"
        @cancel="closeCreateModal"/>
    </modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import CreateUpdateSerpPopup from './CreateUpdateSerpPopup'
import _ from 'lodash'
import BaseSortMixins from '@/common/BaseSortMixins'

const ITEMS_PER_PAGE = 50
const EMPTY_FILTERS = {
  is_active: [],
  query: '',
  last_indexed_page: {
    operator: null,
    value: null
  },
  last_ran: {
    date_from: null,
    date_to: null
  },
  sort_column: ['last_indexed_page'],
  sort_order: ['desc'],
  page_from: 0,
  page_to: ITEMS_PER_PAGE
}
const PAGINATION_OPTIONS = [
  {
    label: '10',
    value: 10
  },
  {
    label: '25',
    value: 25
  },
  {
    label: '50',
    value: 50
  }
]

// table config
const DEFAULT_CONFIG = [
  {
    label: '',
    property: 'serp-buttons'
  },
  {
    label: 'Active',
    property: 'is_active',
    isSmartInput: false,
    options: [
      {
        label: 'Yes',
        value: '1'
      },
      {
        label: 'No',
        value: '0'
      }
    ],
    isDefaultSort: true
  },
  {
    label: 'Query',
    property: 'query',
    isSmartInput: true
  },
  {
    label: 'Last Indexed Pages',
    property: 'last_indexed_page',
    isSmartInput: true
  },
  {
    label: 'Last Ran',
    property: 'last_ran',
    isSmartInput: false,
    options: []
  }
]

export default {
  name: 'SerpTracker',
  components: { CreateUpdateSerpPopup },
  mixins: [BaseSortMixins],
  data () {
    return {
      isDataFetched: false,
      selectedSerp: null,
      isDataUpdating: false,
      filteredSerps: [],
      sortConfig: [{propertyName: 'last_indexed_page', sortDirection: 'desc'}],
      pageItemsLimit: ITEMS_PER_PAGE,
      totalItems: 0,
      serpTableColumnsConfig: _.cloneDeep(DEFAULT_CONFIG),
      filterParams: _.cloneDeep(EMPTY_FILTERS),
      sortableHeaderRefs: {
        'is_active': 'is_active',
        'query': 'query',
        'last_indexed_page': 'last_indexed_page',
        'last_ran': 'last_ran'
      },
      paginationOptions: _.cloneDeep(PAGINATION_OPTIONS)
    }
  },
  computed: {
    ...mapState('serpTracker', ['serps']),
    dataMissing () {
      return this.isDataFetched && !this.serps.length
    },
    changeSerpModalProps () {
      const changeSerpModalProps = {
        width: 690
      }
      return { ...baseModalProps, ...changeSerpModalProps }
    },
    hasPagination () {
      return (this.isDataFetched && !this.isDataUpdating) && this.totalItems > this.pageItemsLimit
    },
    paginatedSerps () {
      let serps = _.cloneDeep(this.serps)
      // eslint-disable-next-line camelcase
      const { page_from = 0, page_to = 100 } = this.filterParams
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.totalItems = serps.length
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.filteredSerps = serps
      return serps.slice(page_from, page_to)
    }
  },
  watch: {
    clientId: {
      handler (v) {
        v && this.loadData()
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('serpTracker', ['getSerpData', 'createSerp', 'updateSerp', 'deleteSerpById']),
    async loadData () {
      this.isDataFetched && (this.isDataUpdating = true)
      await this.getSerpData({
        clientId: this.clientId,
        filters: this.filterParams
      })
      this.isDataFetched = true
      this.isDataUpdating = false
    },
    pageChanged ({ from, to }) {
      this.filterParams.page_from = from
      this.filterParams.page_to = to
    },
    async createOrUpdateSerp (serp) {
      this.isDataUpdating = true
      try {
        if (serp.id && serp.id > -1) {
          await this.updateSerp({ clientId: this.clientId, serp })
        } else {
          await this.createSerp({ clientId: this.clientId, serp })
        }
        await this.loadData()
        this.closeCreateModal()
      } catch (e) {
        this.$refs['create-serp'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    },
    closeCreateModal () {
      this.selectedSerp = null
      this.$modal.hide('create')
    },
    openCreateModal (serpId) {
      this.selectedSerp = this.serps.find(serp => serp.id === serpId)
      this.$modal.show('create')
    },
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.filterParams = { ...this.filterParams, page_from: 0, page_to: value }
    },
    filterColumn (event, column) {
      this.filterParams[column.property] = event
      this.filterParams.page_from = 0
      this.filterParams.page_to = this.pageItemsLimit
      this.loadData()
    },
    onHeaderCellClicked (cell, direction, declineCall) {
      if (direction === 'clear') {
        this.sortConfig.shift()
        return
      }
      if (this.sortConfig.length) {
        this.sortConfig[0].propertyName = cell.property
        this.sortConfig[0].sortDirection = direction
      } else if (cell) {
        this.sortConfig = [{
          sortDirection: direction,
          propertyName: cell.property
        }]
      }
      this.filterParams = {
        ...this.filterParams,
        sort_column: this.sortConfig.map(item => item.propertyName),
        sort_order: this.sortConfig.map(item => item.sortDirection)
      }
      !declineCall && this.loadData()
    },
    cancelSortingAndFilters: function () {
      const emptyFilters = _.cloneDeep(EMPTY_FILTERS)
      this.filterParams = { ...this.filterParams, ...emptyFilters, page_to: this.pageItemsLimit }
      Object.keys(this.sortableHeaderRefs)
        .forEach(ref => {
          const [element] = this.$refs[ref]
          element.$refs[element.childRef].cancel(true)
        })
      this.sortConfig = []
      !this.isDataUpdating && this.loadData()
    },
    cancelFilter (event, column) {
      this.filterParams[column.property] = null
      this.loadData()
    },
    async deleteSerp (serpId) {
      this.isDataUpdating = true
      try {
        await this.deleteSerpById({ clientId: this.clientId, serpId })
        await this.loadData()
        this.closeCreateModal()
      } catch (e) {
        this.$refs['create-serp'].showErrors(e[0])
      } finally {
        this.isDataUpdating = false
      }
    }

  }
}
</script>

<style scoped lang="scss">

.common-table {
  border-radius: $rfx-default-border-radius;
  overflow: visible;

  &__header {
    background-color: rgba(1, 22, 39, 0.1);
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }

  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
    padding: 0;
    height: 60px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__cell {
    color: $rfx-dark-march;
    height: 60px;
    padding: 0;
    display: flex;
    align-items: center;
    font-size: 14px;
    border-bottom: 1px solid $rfx-light-steel;

    &:first-child {
      padding-left: 25px;
    }
  }

  &__row {
    border-left: 1px solid $rfx-light-steel;
    border-right: 1px solid $rfx-light-steel;
  }
}

.serp {
  position: relative;
  min-height: 200vh !important;

  &__cross {
    font-size: 21px;
    margin-left: 5px;
    margin-top: -2px;
  }

  &__title-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 50%;
    height: 60px;
  }

  &__title-right > *:not(:last-child) {
    margin-right: 30px;
  }

  &__title-left {
    display: flex;
    align-items: center;
  }

  &__title-left > *:not(:last-child) {
    margin-right: 30px;
  }

  &__header-button-wrapper {
    border-radius: 15px;
    border: solid 1px $rfx-light-steel;
    background: white;
    height: 28px;
    display: flex;
    padding: 0 10px;
  }

  &__copy-button {
    flex: 1 1 50%;
    height: 100%;
    border: none;
    outline: none;
    color: $rfx-dark-march;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 90px;
    background-color: #fff;
    font-size: 11px;

    &:hover {
      opacity: 0.5;
    }
  }

  &__clear-filters {
    @extend .serp__copy-button;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: auto;

    span:first-child {
      font-size: 11px;
    }
  }

  &__grid-select {
    flex-basis: 100px;
  }

  &__title-container {
    padding: 14px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__loader {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    height: calc(100vh - 530px);
  }

  &__table {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__update-loader {
    position: absolute;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    background-color: white;
    top: 0;
    opacity: 0.5;
    z-index: 10000;
  }

  &__update-loader-ring {
    margin-top: 200px;
  }

  &__serps {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }

  &__query,
  &__last_ran {
    flex: 1 1 150px;
  }

  &__last_indexed_page,
  &__last_ran {
    flex: 1 !important;
  }

  &__serp-buttons {
    &.common-table__header-cell {
      flex-basis: 0;
    }
  }

  &__is_active {

    &.common-table__header-cell {
      flex: 0 0 310px;

      &::v-deep .multi-select__title-container {
        padding-left: 174px;
      }
    }
  }

  &__query {
    flex: 1 1 180px;
  }

  &__serp-buttons,
  &__last_indexed_page {
    flex: 0 1 150px;
  }

  &__row_is_active {
    flex: 0 0 135px;
  }

  &__row_last_indexed_page,
  &__row_last_ran,
  &__row_query {
    padding-left: 15px;
  }

  &__edit-button {
    flex: 0 0 45px;
  }

  &__url_serp {
    flex-basis: 145px;
    color: $rfx-dark-march;
    font-weight: 500;
  }

  &__button {
    width: 136px;
    padding: 0 20px;
    height: 60px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: $rfx-medium-march;
    color: #fff;
  }

  &__count {
    color: #b2b9be;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    width: 75px;
    white-space: nowrap;
  }
}

.pagination-wrapper {
  display: flex;
  height: 60px;
  margin-top: 10px;
  justify-content: center;
  align-items: center;
  border: 1px solid $rfx-light-steel;
  border-radius: $rfx-default-border-radius;
}

.no-data-block {
  border-bottom: 1px solid $rfx-light-steel;
}

.common-table {
  &__header-cell {
    color: $rfx-dark-march;
    font-weight: bold;
  }
}

.edit-icon {
  cursor: pointer;
  font-size: 20px;
  justify-self: end;
  display: grid;
  color: $rfx-dark-march;
}

</style>
